import { NO, UIButton, UIColor, UITextView, UITooltip, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { CBDataViewCellDescriptorWithObject } from "../CB/CBDataView"
import { RHColors, RHRadius, RHSpacing, RHTransition } from "./Utils_slop/RHDesignTokens"


/**
 * RHFavoriteColumn
 *
 * The shared leading "favorite" (lemmik) star column used by every procurement
 * list (procurer dashboard, offerer "Hanked", global "Otsing"). A single toggle
 * per row: a muted grey outline star ☆ when not favorited, a gold filled star ★
 * when favorited. Clicking flips the personal favorite through RHCore (optimistic
 * + broadcast), so every open list repaints. The star is icon-only and narrow, so
 * it reads as a quiet marker rather than a column that competes with the data.
 *
 * The favorites column uses a custom button view, so row reuse only flips a plain
 * star glyph instead of reparsing SVG markup through a text label.
 */

const _STAR_ICON_SIZE = 19
const _STAR_OUTLINE_TEXT = "☆"
const _STAR_FILLED_TEXT = "★"


class RHFavoriteButtonView extends UIButton {
    
    procurementId?: string
    _isFavorite = NO
    
    constructor() {
        super()
        
        this.contentPadding = 0
        this.imageView.hidden = YES
        ;(this as UIButton).configureWithObject({
            nativeSelectionEnabled: NO,
            colors: {
                titleLabel: {
                    normal: UIColor.transparentColor,
                    hovered: UIColor.transparentColor,
                    focused: UIColor.transparentColor,
                    highlighted: UIColor.transparentColor,
                    selected: UIColor.transparentColor
                },
                background: {
                    normal: UIColor.transparentColor,
                    hovered: UIColor.transparentColor,
                    focused: UIColor.transparentColor,
                    highlighted: UIColor.transparentColor,
                    selected: UIColor.transparentColor
                }
            },
            style: {
                cursor: "pointer",
                borderRadius: `${RHRadius.medium}px`,
                transition: `background-color ${RHTransition.fast}`
            }
        })
        
        this.titleLabel.configureWithObject({
            nativeSelectionEnabled: NO,
            userInteractionEnabled: NO,
            textAlignment: UITextView.textAlignment.center,
            text: _STAR_OUTLINE_TEXT,
            style: {
                fontSize: _STAR_ICON_SIZE + "px",
                lineHeight: "1",
                transition: `color ${RHTransition.fast}`
            }
        })
        
        this.viewHTMLElement.setAttribute("role", "button")
        this.viewHTMLElement.setAttribute("aria-label", "Lemmik")
        
        this.addTargetForControlEvent(UIButton.controlEvent.PrimaryActionTriggered, () => {
            if (!this.procurementId) {
                return
            }
            RHCore.sharedInstance.toggleProcurementFavorite(this.procurementId)
        })
        
        this._updateIconPresentation()
    }

    configureForProcurement(procurementId: string | undefined, isFavorite: boolean) {
        this.procurementId = procurementId
        this._isFavorite = isFavorite
        this.selected = isFavorite
        this._updateIconPresentation()
        this.hoverText = ""
        let actionText = "Märgi lemmikuks"
        if (isFavorite) {
            actionText = "Eemalda lemmikutest"
        }
        this.viewHTMLElement.setAttribute("aria-label", actionText)
        if (procurementId) {
            UITooltip.sharedInstance.attach(this, actionText)
        }
        else {
            UITooltip.sharedInstance.detach(this)
        }
    }
    
    override updateContentForCurrentState() {
        super.updateContentForCurrentState()
        this._updateIconPresentation()
    }
    
    _updateIconPresentation() {
        let iconColor = RHColors.slateLight
        let backgroundColor = UIColor.transparentColor
        if (this._isFavorite) {
            iconColor = RHColors.ratingGold
        }
        if (this.hovered || this.focused) {
            backgroundColor = RHColors.warningAmberSurfaceHover
            if (this._isFavorite) {
                iconColor = RHColors.warningAmberHover
            }
            else {
                iconColor = RHColors.ratingGold
            }
        }
        if (this.highlighted) {
            backgroundColor = RHColors.warningAmberSurfacePressed
            iconColor = RHColors.warningAmberActive
        }
        let iconText = _STAR_OUTLINE_TEXT
        if (this._isFavorite) {
            iconText = _STAR_FILLED_TEXT
        }
        
        if (this.titleLabel.text != iconText) {
            this.titleLabel.text = iconText
        }
        this.titleLabel.textColor = iconColor
        this.backgroundColor = backgroundColor
        this.viewHTMLElement.setAttribute("aria-pressed", this._isFavorite + "")
    }

    override intrinsicContentHeight(): number {
        return _STAR_ICON_SIZE
    }
    
    override intrinsicContentWidth(): number {
        return _STAR_ICON_SIZE
    }
    
}


export class RHFavoriteColumn {

    /**
     * The leading favorite-star column descriptor for a procurement list.
     * `procurementIdProvider` extracts the procurement id from a data item (the row shapes
     * differ slightly between the procurer, offerer and search lists).
     */
    static descriptorWithProcurementIdProvider<DataItem extends object>(
        procurementIdProvider: (dataItem: DataItem) => string | undefined
    ) {
        return CBDataViewCellDescriptorWithObject<DataItem>({
            // No header label — the star column is a quiet leading marker, not a titled column.
            title: "",
            keyPath: "_id",
            weight: 0.5,
            absoluteWidth: RHSpacing.xLarge,
            alignment: UITextView.textAlignment.center,
            allowSorting: NO,
            initCustomView: () => new RHFavoriteButtonView(),
            updateCell: (cellView, dataItem) => {
                const procurementId = procurementIdProvider(dataItem)
                let isFavorite = NO
                if (procurementId) {
                    isFavorite = RHCore.sharedInstance.isProcurementFavorite(procurementId)
                }

                const button = cellView.customView as RHFavoriteButtonView | undefined
                if (!button) {
                    return
                }
                button.configureForProcurement(procurementId, isFavorite)
            },
        })
    }

}
